import React, { useMemo, useState } from 'react';

const WIDTH = 1120;
const HEIGHT = 420;
const PADDING = { top: 38, right: 24, bottom: 46, left: 62 };
const ANNUAL_PERIODS = ['previous', 'latest', 'forecast'];
const ANNUAL_PERIOD_META = {
    previous: { calendar: '2024A', fiscal: 'FY2025A' },
    latest: { calendar: '2025A', fiscal: 'FY2026A' },
    forecast: { calendar: '2026E', fiscal: 'FY2027E' },
};
const formatQuarterTick = (date, isZh) => {
    const parsed = new Date(`${date}T00:00:00Z`);
    const quarter = Math.floor(parsed.getUTCMonth() / 3) + 1;
    return isZh
        ? `${String(parsed.getUTCFullYear()).slice(-2)}Q${quarter}`
        : `Q${quarter} '${String(parsed.getUTCFullYear()).slice(-2)}`;
};

const formatValue = (value) => `$${value.toFixed(value >= 10 ? 1 : 2)}B`;

export const buildAnnualCapexStacks = (annualSeries = [], companies = []) => ANNUAL_PERIODS.map((period) => {
    const rows = companies.map((company) => {
        const point = annualSeries.find((candidate) => candidate.period === period && candidate.company === company.id);
        return point ? { ...point, companyMeta: company } : null;
    }).filter(Boolean);
    const total = rows.reduce((sum, row) => sum + row.value, 0);
    return {
        period,
        rows: rows.map((row) => ({ ...row, share: total ? row.value / total : 0 })),
        total,
        evidenceType: period === 'forecast' ? 'forecast' : 'actual',
    };
});

const HyperscalerCapexChart = ({ dataset, isZh = false }) => {
    const companies = useMemo(() => dataset?.companies || [], [dataset]);
    const [selectedCompanies, setSelectedCompanies] = useState(() => new Set(companies.map(({ id }) => id)));
    const [activePoint, setActivePoint] = useState(null);
    const [activeAnnualPeriod, setActiveAnnualPeriod] = useState(null);
    const [grain, setGrain] = useState('quarterly');
    const companyMap = useMemo(() => new Map(companies.map((company) => [company.id, company])), [companies]);
    const visible = companies.filter(({ id }) => selectedCompanies.has(id));
    const points = (dataset?.series || []).filter(({ company }) => selectedCompanies.has(company));
    const annualPoints = (dataset?.annualSeries || []).filter(({ company }) => selectedCompanies.has(company));

    if (!points.length && !annualPoints.length) return null;

    const plotWidth = WIDTH - PADDING.left - PADDING.right;
    const plotHeight = HEIGHT - PADDING.top - PADDING.bottom;
    const timestamps = (dataset?.series || []).map(({ date }) => new Date(`${date}T00:00:00Z`).getTime());
    const minDate = Math.min(...timestamps);
    const maxDate = Math.max(...timestamps);
    const maxValue = Math.ceil(Math.max(...(dataset?.series || []).map(({ value }) => value)) / 10) * 10;
    const x = (date) => PADDING.left + ((new Date(`${date}T00:00:00Z`).getTime() - minDate) / (maxDate - minDate || 1)) * plotWidth;
    const y = (value) => PADDING.top + (1 - value / maxValue) * plotHeight;
    const forecastStart = Math.min(...(dataset?.series || []).filter(({ evidenceType }) => evidenceType === 'forecast').map(({ date }) => new Date(`${date}T00:00:00Z`).getTime()));
    const forecastX = PADDING.left + ((forecastStart - minDate) / (maxDate - minDate || 1)) * plotWidth;
    const yTicks = Array.from({ length: 5 }, (_, index) => (maxValue / 4) * index);
    const xTicks = Array.from({ length: 7 }, (_, index) => minDate + (index / 6) * (maxDate - minDate));

    const groups = visible.map((company) => {
        const rows = points.filter((point) => point.company === company.id).sort((a, b) => a.date.localeCompare(b.date));
        const actual = rows.filter(({ evidenceType }) => evidenceType === 'actual');
        const forecast = rows.filter(({ evidenceType }) => evidenceType === 'forecast');
        const line = (values) => values.map((point, index) => `${index ? 'L' : 'M'} ${x(point.date).toFixed(2)} ${y(point.value).toFixed(2)}`).join(' ');
        const forecastBridge = actual.length && forecast.length ? [actual[actual.length - 1], ...forecast] : forecast;
        return { company, rows, actualPath: line(actual), forecastPath: line(forecastBridge) };
    });
    const annualStacks = buildAnnualCapexStacks(dataset?.annualSeries || [], visible);
    const annualMaxValue = Math.ceil(Math.max(...annualStacks.map(({ total }) => total), 1) * 1.1 / 100) * 100;
    const annualY = (value) => PADDING.top + (1 - value / annualMaxValue) * plotHeight;
    const annualTicks = Array.from({ length: 5 }, (_, index) => (annualMaxValue / 4) * index);
    const annualGroupWidth = plotWidth / ANNUAL_PERIODS.length;
    const annualBarWidth = Math.min(156, annualGroupWidth * 0.5);
    const annualPeriodLabel = (period) => {
        const meta = ANNUAL_PERIOD_META[period];
        return `${meta.calendar} / Microsoft · Oracle ${meta.fiscal}`;
    };

    const toggleCompany = (companyId) => {
        setSelectedCompanies((current) => {
            if (current.has(companyId) && current.size === 1) return new Set(companies.map(({ id }) => id));
            const next = new Set(current);
            if (next.has(companyId)) next.delete(companyId);
            else next.add(companyId);
            return next;
        });
        setActivePoint(null);
        setActiveAnnualPeriod((current) => grain === 'annual' ? current || 'forecast' : null);
    };

    const detailCompany = activePoint ? companyMap.get(activePoint.company) : null;
    const detailKind = activePoint?.evidenceType === 'forecast'
        ? (isZh ? 'InvestFlow 基准预测' : 'InvestFlow base-case estimate')
        : (isZh ? '公司申报实际值' : 'reported actual');
    const activeAnnualStack = annualStacks.find(({ period }) => period === activeAnnualPeriod) || null;

    return (
        <div className="ai-dev-capex-chart">
            <div className="ai-dev-capex-chart__toolbar">
                <div className="ai-dev-capex-chart__companies" aria-label={isZh ? '筛选公司' : 'Filter companies'}>
                    {companies.map((company) => (
                        <button
                            type="button"
                            key={company.id}
                            className={selectedCompanies.has(company.id) ? 'is-active' : ''}
                            style={{ '--company-color': company.color }}
                            aria-pressed={selectedCompanies.has(company.id)}
                            onClick={() => toggleCompany(company.id)}
                        >
                            <i style={{ background: company.color }} />{company.label}
                        </button>
                    ))}
                </div>
                <div className="ai-dev-capex-chart__actions">
                    <div className="ai-dev-capex-chart__view" role="group" aria-label={isZh ? '时间粒度' : 'Time grain'}>
                        {[
                            ['quarterly', isZh ? '季度' : 'Quarterly'],
                            ['annual', isZh ? '年度' : 'Annual'],
                        ].map(([value, label]) => (
                            <button
                                type="button"
                                key={value}
                                className={grain === value ? 'is-active' : ''}
                                aria-pressed={grain === value}
                                onClick={() => {
                                    setGrain(value);
                                    setActivePoint(null);
                                    setActiveAnnualPeriod(value === 'annual' ? 'forecast' : null);
                                }}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                    <div className="ai-dev-capex-chart__key" aria-label={isZh ? '实际与预测图例' : 'Actual and forecast legend'}>
                        <span><i />{isZh ? '实际' : 'Actual'}</span>
                        <span><i className="is-forecast" />{isZh ? '预测' : 'Forecast'}</span>
                    </div>
                </div>
            </div>
            <div className="ai-dev-capex-chart__scroll">
                <div className="ai-dev-capex-chart__plot">
                    <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} role="img" aria-label={grain === 'annual' ? (isZh ? '主要云厂商年度资本开支实际值和预测图' : 'Annual hyperscaler capital expenditure actuals and forecasts') : (isZh ? '主要云厂商季度资本开支实际值和预测图' : 'Quarterly hyperscaler capital expenditure actuals and forecasts')}>
                        {grain === 'quarterly' ? (
                            <>
                                <rect className="ai-dev-capex-chart__forecast-zone" x={forecastX} y={PADDING.top} width={WIDTH - PADDING.right - forecastX} height={plotHeight} />
                                {yTicks.map((tick) => (
                                    <g key={tick}>
                                        <line className="ai-dev-capex-chart__grid" x1={PADDING.left} x2={WIDTH - PADDING.right} y1={y(tick)} y2={y(tick)} />
                                        <text className="ai-dev-capex-chart__tick" x={PADDING.left - 12} y={y(tick) + 4} textAnchor="end">${tick.toFixed(0)}B</text>
                                    </g>
                                ))}
                                {xTicks.map((tick, index) => {
                                    const date = new Date(tick).toISOString().slice(0, 10);
                                    return <text className="ai-dev-capex-chart__tick" key={tick} x={PADDING.left + (index / 6) * plotWidth} y={HEIGHT - 17} textAnchor={index === 0 ? 'start' : index === 6 ? 'end' : 'middle'}>{formatQuarterTick(date, isZh)}</text>;
                                })}
                                <line className="ai-dev-capex-chart__cutoff" x1={forecastX} x2={forecastX} y1={PADDING.top} y2={PADDING.top + plotHeight} />
                                <text className="ai-dev-capex-chart__zone-label" x={Math.min(forecastX + 12, WIDTH - 124)} y={PADDING.top + 16}>{isZh ? '未来四季预测' : '4Q OUTLOOK'}</text>
                                {groups.map(({ company, rows, actualPath, forecastPath }) => (
                                    <g key={company.id}>
                                        {actualPath && <path className="ai-dev-capex-chart__line" d={actualPath} stroke={company.color} />}
                                        {forecastPath && <path className="ai-dev-capex-chart__line is-forecast" d={forecastPath} stroke={company.color} />}
                                        {rows.map((point) => (
                                            <g
                                                className={`ai-dev-capex-chart__point ${point.evidenceType === 'forecast' ? 'is-forecast' : ''}`}
                                                key={`${company.id}-${point.date}`}
                                                tabIndex="0"
                                                role="button"
                                                aria-label={`${company.label}, ${point.quarter}, ${point.evidenceType === 'forecast' ? (isZh ? '预测' : 'forecast') : (isZh ? '实际' : 'actual')}, ${formatValue(point.value)}`}
                                                onMouseEnter={() => setActivePoint(point)}
                                                onFocus={() => setActivePoint(point)}
                                                onClick={() => setActivePoint(point)}
                                            >
                                                <circle cx={x(point.date)} cy={y(point.value)} r="4" fill={company.color} stroke={company.color} />
                                            </g>
                                        ))}
                                    </g>
                                ))}
                            </>
                        ) : (
                            <>
                                <rect className="ai-dev-capex-chart__forecast-zone" x={PADDING.left + annualGroupWidth * 2} y={PADDING.top} width={annualGroupWidth} height={plotHeight} />
                                {annualTicks.map((tick) => (
                                    <g key={tick}>
                                        <line className="ai-dev-capex-chart__grid" x1={PADDING.left} x2={WIDTH - PADDING.right} y1={annualY(tick)} y2={annualY(tick)} />
                                        <text className="ai-dev-capex-chart__tick" x={PADDING.left - 12} y={annualY(tick) + 4} textAnchor="end">${tick.toFixed(0)}B</text>
                                    </g>
                                ))}
                                {ANNUAL_PERIODS.map((period, periodIndex) => (
                                    <text className="ai-dev-capex-chart__tick ai-dev-capex-chart__annual-period-label" key={period} x={PADDING.left + annualGroupWidth * (periodIndex + 0.5)} y={HEIGHT - 24} textAnchor="middle">
                                        <tspan className="ai-dev-capex-chart__annual-period-main" x={PADDING.left + annualGroupWidth * (periodIndex + 0.5)}>{ANNUAL_PERIOD_META[period].calendar}</tspan>
                                        <tspan className="ai-dev-capex-chart__annual-period-fiscal" x={PADDING.left + annualGroupWidth * (periodIndex + 0.5)} dy="13">Microsoft · Oracle {ANNUAL_PERIOD_META[period].fiscal}</tspan>
                                    </text>
                                ))}
                                <text className="ai-dev-capex-chart__zone-label" x={PADDING.left + annualGroupWidth * 2 + 12} y={PADDING.top + 16}>{isZh ? '年度预测' : 'ANNUAL OUTLOOK'}</text>
                                <defs>
                                    <pattern id="capexAnnualForecast" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                                        <rect width="3" height="8" fill="rgba(255,255,255,0.34)" />
                                    </pattern>
                                </defs>
                                {annualStacks.map((stack, periodIndex) => {
                                    const barX = PADDING.left + annualGroupWidth * periodIndex + (annualGroupWidth - annualBarWidth) / 2;
                                    let cumulative = 0;
                                    const segments = stack.rows.map((row) => {
                                        cumulative += row.value;
                                        const segmentY = annualY(cumulative);
                                        const segmentHeight = (row.value / annualMaxValue) * plotHeight;
                                        return { ...row, segmentY, segmentHeight, centerY: segmentY + segmentHeight / 2 };
                                    });
                                    const labelsTopDown = [...segments].sort((a, b) => a.centerY - b.centerY);
                                    let previousLabelY = -Infinity;
                                    const labelYMap = new Map(labelsTopDown.map((row) => {
                                        const labelY = Math.max(row.centerY, previousLabelY + 13);
                                        previousLabelY = labelY;
                                        return [row.company, labelY];
                                    }));
                                    return (
                                        <g
                                            className={`ai-dev-capex-chart__annual-stack ${stack.evidenceType === 'forecast' ? 'is-forecast' : ''}`}
                                            key={stack.period}
                                            tabIndex="0"
                                            role="button"
                                            aria-label={`${annualPeriodLabel(stack.period)}, ${isZh ? '所选公司合计' : 'selected-company total'} ${formatValue(stack.total)}, ${stack.rows.length} ${isZh ? '家公司' : 'companies'}`}
                                            onMouseEnter={() => setActiveAnnualPeriod(stack.period)}
                                            onFocus={() => setActiveAnnualPeriod(stack.period)}
                                            onClick={() => setActiveAnnualPeriod(stack.period)}
                                        >
                                            {segments.map((row) => (
                                                <g key={row.company}>
                                                    <rect className="ai-dev-capex-chart__annual-segment" x={barX} y={row.segmentY} width={annualBarWidth} height={row.segmentHeight} fill={row.companyMeta.color} />
                                                    <path
                                                        className="ai-dev-capex-chart__annual-leader"
                                                        d={`M ${barX + annualBarWidth} ${row.centerY} L ${barX + annualBarWidth + 7} ${labelYMap.get(row.company)} L ${barX + annualBarWidth + 11} ${labelYMap.get(row.company)}`}
                                                        stroke={row.companyMeta.color}
                                                    />
                                                    <text className="ai-dev-capex-chart__annual-share" x={barX + annualBarWidth + 14} y={labelYMap.get(row.company) + 3}>
                                                        {row.companyMeta.label} {(row.share * 100).toFixed(1)}%
                                                    </text>
                                                </g>
                                            ))}
                                            {stack.evidenceType === 'forecast' ? <rect className="ai-dev-capex-chart__annual-hatch" x={barX} y={annualY(stack.total)} width={annualBarWidth} height={(stack.total / annualMaxValue) * plotHeight} fill="url(#capexAnnualForecast)" /> : null}
                                            <rect className="ai-dev-capex-chart__annual-outline" x={barX} y={annualY(stack.total)} width={annualBarWidth} height={(stack.total / annualMaxValue) * plotHeight} />
                                            <text className="ai-dev-capex-chart__annual-total" x={barX + annualBarWidth / 2} y={annualY(stack.total) - 10} textAnchor="middle">{formatValue(stack.total)}</text>
                                        </g>
                                    );
                                })}
                            </>
                        )}
                    </svg>
                </div>
            </div>
            <div className={`ai-dev-capex-chart__detail ${grain === 'annual' ? 'is-annual' : ''}`} style={{ '--detail-color': detailCompany?.color || '#153f78' }} aria-live="polite">
                <i />
                <div>
                    <strong>{grain === 'annual' && activeAnnualStack
                        ? `${annualPeriodLabel(activeAnnualStack.period)} · ${isZh ? '所选公司合计' : 'selected-company total'}`
                        : activePoint ? `${detailCompany?.label} · ${activePoint.quarter || activePoint.label}` : (isZh ? '指向或点击任一数据点查看口径' : 'Hover or select any point for its definition')}</strong>
                    <span>{grain === 'annual' && activeAnnualStack
                        ? (isZh ? '下方列出每家公司金额及其在合计中的占比。' : 'Company amounts and shares of the selected total are listed below.')
                        : activePoint ? `${detailKind} · ${detailCompany?.definitionZh || detailCompany?.definition}` : (isZh ? '所有实际值采用现金资本开支口径；各公司财务定义仍有差异。' : 'Actuals use cash-capex measures; filing definitions still differ by company.')}</span>
                </div>
                <b>{grain === 'annual' && activeAnnualStack ? formatValue(activeAnnualStack.total) : activePoint ? formatValue(activePoint.value) : (grain === 'annual' ? 'USD / year' : 'USD / quarter')}</b>
            </div>
            {grain === 'annual' && activeAnnualStack ? (
                <div className="ai-dev-capex-chart__annual-breakdown" aria-label={isZh ? '年度资本开支构成' : 'Annual CapEx composition'}>
                    {activeAnnualStack.rows.map((row) => (
                        <span key={row.company}>
                            <i style={{ background: row.companyMeta.color }} />
                            <strong>{row.companyMeta.label}</strong>
                            <span className="ai-dev-capex-chart__annual-company-period">{row.label}</span>
                            <small>
                                <b>{formatValue(row.value)}</b>
                                <em>{(row.share * 100).toFixed(1)}%</em>
                            </small>
                        </span>
                    ))}
                </div>
            ) : null}
            <div className="ai-dev-capex-chart__method">
                <span>{grain === 'annual'
                    ? (isZh ? '年度柱并非统一自然年：Amazon、Alphabet、Meta 按 2024A / 2025A / 2026E；Microsoft、Oracle 分别按 FY2025A / FY2026A / FY2027E。' : 'Annual bars mix reporting calendars: Amazon, Alphabet, and Meta use 2024A / 2025A / 2026E; Microsoft and Oracle use FY2025A / FY2026A / FY2027E.')
                    : (isZh ? '预测：官方年度计划中点 + 已实现季度节奏；Oracle 为趋势基准，并非公司指引。' : 'Forecast: annual-plan midpoints plus reported quarterly pacing; Oracle is a trend base case, not company guidance.')}</span>
                {companies.map((company) => <a key={company.id} href={company.sourceUrl} target="_blank" rel="noreferrer">{company.label}</a>)}
            </div>
        </div>
    );
};

export default HyperscalerCapexChart;
